<template>
  <ModalStep
    :id="'mfa-disable-modal-content'"
    data-test-id="mfa-disable-modal"
    :header="{
      title: t('auth.mfa.disable.title'),
    }"
  >
    <template #bodyContent>
      <form>
        <p class="warning-text">{{ t('auth.mfa.disable.description') }}</p>
        <CustomInput
          id="current-password"
          data-test-id="mfa-disable-password"
          type="password"
          :label="t('auth.mfa.disable.input.password.label')"
          :placeholder="t('auth.mfa.disable.input.password.placeholder')"
          :value="passwordRef"
          @emit:change="passwordChangeHandler"
          :errors="passwordErrorRef"
        />
        <CustomInput
          id="mfa-code"
          data-test-id="mfa-disable-code"
          type="text"
          :label="t('auth.mfa.disable.input.code.label')"
          :placeholder="t('auth.mfa.disable.input.code.placeholder')"
          :value="codeRef"
          @emit:change="codeChangeHandler"
          :errors="codeErrorRef"
          :maxlength="6"
        />
      </form>
    </template>
    <template #footer>
      <div class="c-btns-container">
        <CustomButton
          usage="button"
          type="button"
          color="white"
          @emit:click="closeModal"
          :text="t('ui.button.cancel')"
        ></CustomButton>
        <CustomButton
          usage="button"
          type="button"
          data-test-id="confirm-mfa-disable-button"
          color="danger"
          @emit:click="submitForm"
          :isDisabled="!passwordRef || !codeRef || codeRef.length !== 6"
          :isLoading="isPending"
          :text="t('ui.button.confirm')"
        ></CustomButton>
      </div>
    </template>
  </ModalStep>
</template>

<script setup lang="ts">
import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { ref } from 'vue'

import { mfaDisable } from '@/features/auth/api'
import { V1_USER_DATA_QUERY_KEY, V1_AUTH_USER_QUERY_KEY } from '@/features/auth/hooks/use-account.hook'
import CustomButton from '@/features/ui/components/button/custom-button.vue'
import CustomInput from '@/features/ui/components/input/custom-input.vue'
import ModalStep from '@/features/ui/components/modal/modal-step/modal-step.vue'
import modalsStore from '@/features/ui/store/modal/modal.store'
import useToast from '@/hooks/use-toasts.hook'
import { useI18n } from '@/lib/i18n'

const { t } = useI18n()
const toast = useToast()
const queryClient = useQueryClient()

const passwordRef = ref<string>('')
const codeRef = ref<string>('')
const passwordErrorRef = ref<string>('')
const codeErrorRef = ref<string>('')

const { mutate: disableMfa, isPending } = useMutation({
  mutationFn: mfaDisable,
  onError: (error: any) => {
    if (error.message?.includes('Invalid') || error.status === 400 || error.status === 401) {
      toast?.error(t('auth.mfa.disable.error'))
    } else {
      toast?.error(t('error.common'))
    }
  },
  onSuccess: async () => {
    toast?.success(t('auth.mfa.disable.success'))
    // Invalidate account queries to refresh mfaEnabled status
    await queryClient.invalidateQueries({ queryKey: [V1_USER_DATA_QUERY_KEY] })
    await queryClient.invalidateQueries({ queryKey: [V1_AUTH_USER_QUERY_KEY] })
    modalsStore.toggleModal('mfa-disable', false)
  },
})

function passwordChangeHandler(value: string) {
  passwordRef.value = value
  passwordErrorRef.value = ''
}

function codeChangeHandler(value: string) {
  codeRef.value = value
  codeErrorRef.value = ''
}

function submitForm() {
  if (!passwordRef.value || !codeRef.value || codeRef.value.length !== 6) {
    if (!passwordRef.value) passwordErrorRef.value = t('auth.password.errors.required')
    if (!codeRef.value || codeRef.value.length !== 6) codeErrorRef.value = t('auth.mfa.setup.error')
    return
  }

  disableMfa({
    password: passwordRef.value,
    code: codeRef.value,
  })
}

function closeModal() {
  modalsStore.toggleModal('mfa-disable', false)
}
</script>

<style lang="scss" scoped>
.warning-text {
  font-size: 0.9rem;
  color: rgb(var(--color-badge-warning-fg));
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: rgb(var(--color-badge-warning-bg));
  border-radius: 8px;
  border-left: 4px solid rgb(var(--color-badge-warning-fg));
}
</style>
